import { useCallback, useEffect, useState, type ReactNode } from 'react'
import {
  BarChart3, Download, Gift, History, KeyRound, LogOut, MapPin, Medal, Megaphone, MoreHorizontal, RefreshCw, ScrollText, Settings, Smartphone, Ticket,
  Tablet as TabletIcoon, Trophy, UserCog, Users,
} from 'lucide-react'
import { ApiFout, foutTekst, vraag } from '../lib/api'
import { ga, klikNaar } from '../lib/router'
import type { BeheerVestiging, Ik } from '../lib/typen'
import { ROLNAAM, mag, type Recht } from '../../supabase/functions/_gedeeld/regels.ts'
import { Installeren } from '../onderdelen/Installeren'
import { Lade } from '../onderdelen/Lade'
import { Logo } from '../onderdelen/Logo'
import { Klanten } from './Klanten'
import { Mensen } from './Mensen'
import { Logboek } from './Logboek'
import { Marketing } from './Marketing'
import { Rangen } from './Rangen'
import { Vouchers } from './Vouchers'
import { Beloningen, Chauffeurs, Exporteren, Exportlog, Instellingen } from './Overig'
import { Overzicht, Tablets, Vestigingen } from './Paginas'

export interface Sessie { ik: Ik; vestigingen: BeheerVestiging[]; herlaad: () => Promise<void> }

interface Item { pad: string; naam: string; icoon: ReactNode; recht: Recht | Recht[] }
const ITEMS: Item[] = [
  { pad: 'overzicht', naam: 'Overzicht', icoon: <BarChart3 size={22} />, recht: 'overzicht' },
  { pad: 'logboek', naam: 'Logboek', icoon: <ScrollText size={22} />, recht: 'logboek' },
  { pad: 'tablets', naam: 'Tablets', icoon: <TabletIcoon size={22} />, recht: 'tablets' },
  { pad: 'klanten', naam: 'Klanten', icoon: <Users size={22} />, recht: 'klanten' },
  { pad: 'mensen', naam: 'Medewerkers', icoon: <UserCog size={22} />, recht: 'personen' },
  { pad: 'vestigingen', naam: 'Vestigingen', icoon: <MapPin size={22} />, recht: 'vestigingen' },
  { pad: 'beloningen', naam: 'Beloningen', icoon: <Gift size={22} />, recht: 'beloningen' },
  { pad: 'rangen', naam: 'Rangen', icoon: <Medal size={22} />, recht: 'beloningen' },
  { pad: 'vouchers', naam: 'Vouchers', icoon: <Ticket size={22} />, recht: 'vouchers' },
  { pad: 'chauffeurs', naam: 'Chauffeurs', icoon: <Trophy size={22} />, recht: 'chauffeurs' },
  { pad: 'marketing', naam: 'Marketing', icoon: <Megaphone size={22} />, recht: 'mails' },
  { pad: 'export', naam: 'Export', icoon: <Download size={22} />, recht: ['export-marketing', 'export-klanten', 'export-activiteit'] },
  { pad: 'exportlog', naam: 'Exportlog', icoon: <History size={22} />, recht: 'exportlog' },
  { pad: 'instellingen', naam: 'Instellingen', icoon: <Settings size={22} />, recht: 'instellingen' },
]
const magItem = (ik: Ik, i: Item) => (Array.isArray(i.recht) ? i.recht : [i.recht]).some((r) => mag(ik.rol, r))

export function Beheer({ pad }: { pad: string }) {
  const delen = pad.split('/').filter(Boolean).slice(1)
  const [sessie, zetSessie] = useState<{ ik: Ik; vestigingen: BeheerVestiging[] } | null | 'laden'>('laden')

  const herlaad = useCallback(async () => {
    try { zetSessie(await vraag('ik')) } catch (e) {
      if (e instanceof ApiFout && e.status === 401) zetSessie(null)
      else zetSessie(null)
    }
  }, [])
  useEffect(() => { if (delen[0] !== 'welkom') herlaad() }, [herlaad]) // eslint-disable-line react-hooks/exhaustive-deps

  if (delen[0] === 'welkom') return <Welkom token={delen[1] ?? ''} klaar={() => { herlaad(); ga('/beheer', true) }} />
  if (sessie === 'laden') return <div className="inlog"><RefreshCw className="draai" /></div>
  if (!sessie) return <Inloggen klaar={(s) => { zetSessie(s); ga('/beheer', true) }} />

  const s: Sessie = { ...sessie, herlaad }
  const items = ITEMS.filter((i) => magItem(sessie.ik, i))
  const huidig = delen[0] ?? ''

  if (!huidig) return <Kies s={s} items={items} />
  const item = items.find((i) => i.pad === huidig)
  if (!item) { ga('/beheer', true); return null }

  const pagina: Record<string, ReactNode> = {
    overzicht: <Overzicht s={s} />, tablets: <Tablets s={s} />, klanten: <Klanten s={s} />, mensen: <Mensen s={s} />,
    vestigingen: <Vestigingen s={s} />, beloningen: <Beloningen s={s} />, chauffeurs: <Chauffeurs />, export: <Exporteren s={s} />,
    logboek: <Logboek s={s} />, exportlog: <Exportlog />, instellingen: <Instellingen />, marketing: <Marketing />, rangen: <Rangen />, vouchers: <Vouchers s={s} />,
  }
  return <Schil s={s} items={items} huidig={huidig}>{pagina[huidig]}</Schil>
}

function Schil({ s, items, huidig, children }: { s: Sessie; items: Item[]; huidig: string; children: ReactNode }) {
  const [meer, zetMeer] = useState(false)
  const onder = items.length > 5 ? items.slice(0, 4) : items
  const rest = items.length > 5 ? items.slice(4) : []
  const tab = (i: Item) => (
    <a key={i.pad} href={`/beheer/${i.pad}`} className="tab" aria-current={i.pad === huidig ? 'page' : undefined}
      onClick={(e) => { zetMeer(false); klikNaar(`/beheer/${i.pad}`)(e) }}>{i.icoon}<span>{i.naam}</span></a>
  )
  return (
    <div className="beheer">
      <header className="beheer-kop">
        <a href="/beheer" className="merk" onClick={klikNaar('/beheer')}><Logo vlak /></a>
        <h1 className="vul afkap">TW1 Club <span className="stil">beheer</span></h1>
        <Uitloggen ik={s.ik} />
      </header>
      <div className="beheer-romp">
        <nav className="zijbalk" aria-label="Beheer">{items.map(tab)}</nav>
        <main className="beheer-lijf">{children}</main>
      </div>
      <nav className="onderbalk" aria-label="Beheer">
        {onder.map(tab)}
        {rest.length > 0 && (
          <button className="tab" aria-current={rest.some((r) => r.pad === huidig) ? 'page' : undefined} onClick={() => zetMeer(true)}>
            <MoreHorizontal size={22} /><span>Meer</span>
          </button>
        )}
      </nav>
      <Lade titel="Meer" open={meer} sluit={() => zetMeer(false)} kinderen={
        <div className="keuze-tegels raster">
          {rest.map((i) => (
            <a key={i.pad} href={`/beheer/${i.pad}`} className="keuze-tegel" onClick={(e) => { zetMeer(false); klikNaar(`/beheer/${i.pad}`)(e) }}>
              <span className="beloning-icoon">{i.icoon}</span><b>{i.naam}</b>
            </a>
          ))}
        </div>
      } />
    </div>
  )
}

function Uitloggen({ ik }: { ik: Ik }) {
  const [open, zet] = useState(false)
  return (
    <>
      <button className="knop stil-knop klein-knop" onClick={() => zet(true)} aria-label="Mijn account">
        <span className="badge geel">{ik.naam.split(' ').map((d) => d[0]).join('').slice(0, 2).toUpperCase()}</span>
      </button>
      <Lade titel={ik.naam} open={open} sluit={() => zet(false)} kinderen={
        <div className="lijst">
          <p className="stil">{ik.email} · {ROLNAAM[ik.rol]}</p>
          <Installeren naam="TW1 Club Beheer" tekst="Beheer als app op dit apparaat" />
          <a className="knop breed" href="/" onClick={klikNaar('/')}><Smartphone size={19} />Naar het klantdashboard</a>
          <button className="knop gevaar breed" onClick={async () => { try { await vraag('uitloggen') } finally { location.href = '/beheer' } }}>
            <LogOut size={19} />Uitloggen
          </button>
        </div>
      } />
    </>
  )
}

/* Na het inloggen: kiezen, alleen uit wat je mag (docs/toegang.md). */
function Kies({ s, items }: { s: Sessie; items: Item[] }) {
  const beheer = items.filter((i) => !['export', 'marketing', 'vouchers'].includes(i.pad))
  const marketing = mag(s.ik.rol, 'mails')
  useEffect(() => {
    if (!beheer.length && marketing) ga('/beheer/marketing', true)
  }, [beheer.length, marketing])
  return (
    <div className="inlog">
      <div className="kaart">
        <Logo vlak />
        <div><h1>Hoi {s.ik.naam.split(' ')[0]}</h1><p className="stil">{ROLNAAM[s.ik.rol]} · waar wil je heen?</p></div>
        <div className="keuze-tegels">
          {beheer.length > 0 && (
            <a className="keuze-tegel" href={`/beheer/${beheer[0]?.pad}`} onClick={klikNaar(`/beheer/${beheer[0]?.pad}`)}>
              <span className="beloning-icoon"><BarChart3 size={24} /></span>
              <span><b>Beheer</b><small>Tablets, medewerkers, vestigingen, cijfers</small></span>
            </a>
          )}
          {marketing && (
            <a className="keuze-tegel" href="/beheer/marketing" onClick={klikNaar('/beheer/marketing')}>
              <span className="beloning-icoon"><Megaphone size={24} /></span>
              <span><b>Marketing</b><small>Mails, verjaardagen en vouchers</small></span>
            </a>
          )}
          <a className="keuze-tegel" href="/" onClick={klikNaar('/')}>
            <span className="beloning-icoon"><Smartphone size={24} /></span>
            <span><b>Klantdashboard</b><small>Zoals een chauffeur het ziet</small></span>
          </a>
        </div>
      </div>
    </div>
  )
}

function Inloggen({ klaar }: { klaar: (s: { ik: Ik; vestigingen: BeheerVestiging[] }) => void }) {
  const [email, zetEmail] = useState('')
  const [ww, zetWw] = useState('')
  const [bezig, zetBezig] = useState(false)
  const [fout, zetFout] = useState<string | null>(null)
  const inloggen = async () => {
    zetBezig(true); zetFout(null)
    try {
      await vraag('inloggen', { email, wachtwoord: ww })
      klaar(await vraag('ik'))
    } catch (e) { zetFout(foutTekst(e)) } finally { zetBezig(false) }
  }
  return (
    <div className="inlog">
      <form className="kaart" onSubmit={(e) => { e.preventDefault(); inloggen() }}>
        <a href="/" onClick={klikNaar('/')}><Logo vlak /></a>
        <div><h1>Inloggen</h1><p className="stil">Voor managers, MT en marketing. Medewerkers gebruiken hun PIN op de tablet.</p></div>
        <label className="veld"><span>E-mail</span>
          <input type="email" inputMode="email" autoComplete="username" value={email} onChange={(e) => zetEmail(e.target.value)} required />
        </label>
        <label className="veld"><span>Wachtwoord</span>
          <input type="password" autoComplete="current-password" value={ww} onChange={(e) => zetWw(e.target.value)} required />
        </label>
        {fout && <div className="melding fout">{fout}</div>}
        <button className="knop geel breed" disabled={bezig}>{bezig ? 'Inloggen…' : 'Inloggen'}</button>
        <p className="stil klein">Wachtwoord vergeten? Vraag je OM of MT om een nieuwe uitnodigingslink.</p>
      </form>
    </div>
  )
}

function Welkom({ token, klaar }: { token: string; klaar: () => void }) {
  const [wie, zetWie] = useState<{ naam: string; email: string } | null>(null)
  const [fout, zetFout] = useState<string | null>(null)
  const [ww, zetWw] = useState('')
  const [ww2, zetWw2] = useState('')
  const [bezig, zetBezig] = useState(false)
  useEffect(() => { vraag('uitnodiging', { token }).then(zetWie, (e) => zetFout(foutTekst(e))) }, [token])
  const zet = async () => {
    if (ww !== ww2) { zetFout('De twee wachtwoorden zijn niet gelijk.'); return }
    zetBezig(true); zetFout(null)
    try { await vraag('wachtwoord-zetten', { token, wachtwoord: ww }); klaar() } catch (e) { zetFout(foutTekst(e)) } finally { zetBezig(false) }
  }
  return (
    <div className="inlog">
      <form className="kaart" onSubmit={(e) => { e.preventDefault(); zet() }}>
        <Logo vlak />
        <div><h1><KeyRound size={22} style={{ display: 'inline', verticalAlign: '-3px' }} /> Kies je wachtwoord</h1>
          {wie && <p className="stil">Welkom {wie.naam} ({wie.email}).</p>}</div>
        {wie && <>
          <input type="email" name="email" value={wie.email} autoComplete="username" readOnly className="sr" tabIndex={-1} aria-hidden="true" />
          <label className="veld"><span>Nieuw wachtwoord</span>
            <input type="password" autoComplete="new-password" value={ww} onChange={(e) => zetWw(e.target.value)} minLength={10} required />
            <small>Minstens 10 tekens. Een zin werkt goed.</small>
          </label>
          <label className="veld"><span>Nog een keer</span>
            <input type="password" autoComplete="new-password" value={ww2} onChange={(e) => zetWw2(e.target.value)} required />
          </label>
        </>}
        {fout && <div className="melding fout">{fout}</div>}
        {wie && <button className="knop geel breed" disabled={bezig}>{bezig ? 'Opslaan…' : 'Opslaan en inloggen'}</button>}
      </form>
    </div>
  )
}

/* ------------------------------------------------------------------ */

/** Een actie laden, met opnieuw-knop. */
export function useLaad<T>(actie: string, lijf: Record<string, unknown> = {}) {
  const [data, zet] = useState<T | null>(null)
  const [fout, zetFout] = useState<string | null>(null)
  const sleutel = JSON.stringify(lijf)
  const laad = useCallback(async () => {
    zetFout(null)
    try { zet(await vraag<T>(actie, JSON.parse(sleutel))) } catch (e) { zetFout(foutTekst(e)) }
  }, [actie, sleutel])
  useEffect(() => { laad() }, [laad])
  return { data, fout, laad, zet }
}

export function Laden({ fout, opnieuw }: { fout: string | null; opnieuw: () => void }) {
  if (fout) return <div className="melding fout"><span className="vul">{fout}</span><button className="knop klein-knop" onClick={opnieuw}>Opnieuw</button></div>
  return <div className="leeg-staat"><RefreshCw className="draai" /></div>
}
